import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft } from 'lucide-react';
import BookCallButton from '../components/BookCallButton';
import { SparklesCore } from '../components/ui/sparkles';
import Navigation from '../components/Navigation';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (delay: number = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1], delay },
  }),
};

const AboutPage: React.FC = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, []);

  useEffect(() => {
    const metas = [
      { name: 'prerender-status-code', content: '200' },
      { name: 'fragment', content: '!' },
      { name: 'renderer', content: 'webkit' },
    ].map(({ name, content }) => {
      const m = document.createElement('meta');
      m.name = name;
      m.content = content;
      document.head.appendChild(m);
      return m;
    });
    return () => metas.forEach(m => document.head.removeChild(m));
  }, []);

  const teamMembers = [
    {
      image: '/team/alex-martinez.jpg',
      name: 'Joseph Carson',
      role: 'Founder',
      subRole: 'Director of Sales & Strategic Partnerships',
      intro: 'Joseph is the relationship at the center of every client success story.',
      description:
        "He drives Terra Nexus AI's growth through strategic sales and client-focused business development, managing every step of the lifecycle — from the first conversation through onboarding and beyond. Joseph makes sure every client feels supported, not sold to.",
    },
    {
      image: '/team/michael-thompson.jpg',
      name: 'KaCey Dempster',
      role: 'Founder',
      subRole: 'Director of Web & AI Development',
      intro: 'KaCey is the technical force that turns ideas into working products.',
      description:
        "He leads the development and deployment of custom websites and AI solutions, overseeing architecture, infrastructure, and engineering from the ground up. From quality assurance to cutting-edge integrations, KaCey makes sure everything we ship is built to last.",
    },
    {
      image: '/team/IMG_6819.jpeg',
      name: 'Cole Hayduk',
      role: 'Founder',
      subRole: 'Director of Sales & Strategic Partnerships',
      intro: 'Cole brings the energy and hustle that keeps Terra Nexus moving forward.',
      description:
        "Alongside Joseph, Cole drives growth through strategic outreach and hands-on client partnerships. He manages the full client journey — making sure every business that works with us walks away with results they can point to.",
    },
  ];

  return (
    <div className="min-h-screen overflow-x-hidden">
      <Navigation />
      <Helmet>
        <title>About Terra Nexus AI | AI-Powered Business Solutions</title>
        <meta name="description" content="Learn about Terra Nexus AI's mission to democratize AI technology and empower businesses with intelligent automation solutions." />
        <meta name="robots" content="index, follow, all, max-image-preview:large" />
        <link rel="canonical" href="https://terranexusai.com/about" />
        <meta property="og:title" content="About Terra Nexus AI | AI-Powered Business Solutions" />
        <meta property="og:description" content="Learn about Terra Nexus AI's mission to democratize AI technology and empower businesses with intelligent automation solutions." />
        <meta property="og:url" content="https://terranexusai.com/about" />
        <meta name="twitter:card" content="summary" />
      </Helmet>

      {/* ─── HERO (black + stars) ──────────────────────────────────── */}
      <section className="relative bg-black text-white overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <SparklesCore
            id="aboutpage-sparkles"
            background="transparent"
            minSize={0.4}
            maxSize={1.0}
            particleDensity={28}
            className="w-full h-full"
            particleColor="#D4AF37"
            speed={0.3}
          />
        </div>
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] rounded-full bg-gold-500/8 blur-[120px]" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-gold-400/70 hover:text-gold-300 transition-colors text-sm tracking-wide"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 pt-16 pb-32 text-center">
          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={0}
            className="text-gold-400 text-xs tracking-[0.3em] uppercase font-medium mb-6"
          >
            Our Story
          </motion.p>
          <motion.h1
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={0.1}
            className="text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.08] tracking-tight mb-8"
          >
            We believe AI should{' '}
            <span className="gold-gradient bg-clip-text text-transparent gold-glow">
              work for you,
            </span>{' '}
            not the other way around.
          </motion.h1>
          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={0.2}
            className="text-lg md:text-xl text-white/50 leading-relaxed max-w-2xl mx-auto"
          >
            Three founders from Alberta. One mission: make sure small businesses aren't left behind.
          </motion.p>
        </div>

        <div className="absolute bottom-0 inset-x-0 h-16 bg-gradient-to-b from-transparent to-white pointer-events-none" />
      </section>

      {/* ─── WHITE CONTENT ────────────────────────────────────────── */}
      <div className="bg-white">

        {/* ─── ORIGIN PARAGRAPH ────────────────────────────────────── */}
        <section className="py-24 px-6">
          <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
            {/* Left: label + team photo */}
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="flex flex-col gap-5"
            >
              <div>
                <p className="text-gray-500 text-xs tracking-[0.3em] uppercase font-medium mb-3">Who we are</p>
                <div className="w-12 h-px bg-gradient-to-r from-yellow-600 to-yellow-400" />
              </div>
              <div className="aspect-[3/4] rounded-2xl overflow-hidden shadow-[0_8px_40px_rgba(0,0,0,0.14)] bg-gray-100">
                <img
                  src="/images/team-photo.jpg"
                  alt="Terra Nexus AI founding team together outdoors"
                  className="w-full h-full object-cover object-top block"
                />
              </div>
            </motion.div>

            {/* Right: paragraph */}
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              custom={0.1}
            >
              <p className="text-2xl md:text-3xl text-gray-900 font-semibold leading-relaxed">
                Founded by three young entrepreneurs from Alberta, Terra Nexus AI was built on a simple belief:{' '}
                <span className="text-gray-600 font-normal italic">
                  AI is already here, and small businesses should not be left behind.
                </span>
              </p>
              <p className="text-gray-700 text-lg leading-relaxed mt-6">
                We understand how quickly technology is changing, and our mission is to help local businesses
                integrate practical AI systems before it becomes too late to catch up. Through websites,
                chatbots, AI agents, and automations, we help businesses save time, streamline operations,
                and unlock new potential in the way they work.
              </p>
            </motion.div>
          </div>
        </section>

        {/* ─── MISSION / VISION ────────────────────────────────────── */}
        <section className="py-4 px-6 pb-24">
          <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-6">
            {[
              {
                label: 'Mission',
                headline: 'Automation that actually makes sense.',
                body: "We build AI tools that integrate into how your business already works — no steep learning curves, no bloated platforms. Just results. We're here to make sure every business, regardless of size, can compete with the efficiency of the biggest players in the room.",
                items: [
                  'Intuitive AI for any team size',
                  'Automation that reduces real friction',
                  'Technology that grows with you',
                ],
              },
              {
                label: 'Vision',
                headline: 'A future where humans focus on what matters.',
                body: "We see a world where the tedious is handled by machines and the creative is handled by people. We're building toward that future one client at a time — proving that AI implementation doesn't have to be painful.",
                items: [
                  'AI as a natural business layer',
                  'Accessible to every industry',
                  'Long-term partnerships, not transactions',
                ],
              },
            ].map((card, i) => (
              <motion.div
                key={card.label}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i * 0.12}
                whileHover={{ y: -5 }}
                transition={{ type: 'spring', stiffness: 200, damping: 25 }}
                className="group relative bg-gray-950 rounded-2xl p-8 md:p-10 shadow-[0_8px_40px_rgba(0,0,0,0.18)] hover:shadow-[0_16px_56px_rgba(0,0,0,0.24)] transition-shadow duration-300"
              >
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-gold-500/8 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                <div className="absolute top-0 inset-x-0 h-px rounded-t-2xl bg-gradient-to-r from-transparent via-gold-500/30 to-transparent" />
                <p className="text-gold-400 text-xs tracking-[0.25em] uppercase font-medium mb-4">{card.label}</p>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-5 leading-snug">{card.headline}</h2>
                <p className="text-white/50 leading-relaxed mb-7">{card.body}</p>
                <ul className="space-y-3">
                  {card.items.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-gold-400 shrink-0" />
                      <span className="text-white/70 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </section>

        {/* ─── TEAM (editorial narrative layout) ───────────────────── */}
        <section className="px-6 pb-24">
          <div className="max-w-6xl mx-auto">

            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="mb-20"
            >
              <p className="text-gray-400 text-xs tracking-[0.3em] uppercase font-medium mb-3">The founders</p>
              <div className="w-12 h-px bg-gradient-to-r from-yellow-600 to-yellow-400 mb-8" />
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 max-w-xl leading-tight">
                The people behind the work
              </h2>
            </motion.div>

            <div className="space-y-6">
              {teamMembers.map((member, i) => {
                const isEven = i % 2 === 0;
                return (
                  <motion.div
                    key={member.name}
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    custom={0.1}
                    className="group relative bg-gray-950 rounded-2xl overflow-hidden shadow-[0_8px_40px_rgba(0,0,0,0.18)] hover:shadow-[0_20px_60px_rgba(0,0,0,0.26)] transition-shadow duration-300 md:min-h-[420px]"
                  >
                    <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-gold-400/35 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10" />

                    <div className={`flex flex-col ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                      {/* Photo */}
                      <div className="relative md:w-[38%] shrink-0 overflow-hidden self-stretch min-h-[300px]">
                          <motion.img
                            src={member.image}
                            alt={member.name}
                            className="absolute inset-0 w-full h-full object-cover object-center"
                            whileHover={{ scale: 1.03 }}
                            transition={{ duration: 0.7, ease: 'easeOut' }}
                          />
                          {/* Directional fade toward text */}
                          <div className={`absolute inset-0 ${isEven
                            ? 'bg-gradient-to-r from-transparent via-transparent to-gray-950/70 hidden md:block'
                            : 'bg-gradient-to-l from-transparent via-transparent to-gray-950/70 hidden md:block'
                          }`} />
                          <div className="absolute inset-0 bg-gradient-to-t from-gray-950/60 to-transparent md:hidden" />
                      </div>

                      {/* Text */}
                      <div className="flex-1 p-8 md:p-12 flex flex-col justify-center">
                        <p className="text-gold-400/60 text-xs tracking-[0.25em] uppercase font-medium mb-4">
                          {member.role} — {member.subRole}
                        </p>

                        <p className="text-xl md:text-2xl font-semibold text-white/80 leading-snug mb-2">
                          {member.intro}
                        </p>

                        <h3 className="text-3xl md:text-4xl font-bold text-white mb-5">
                          {member.name}
                        </h3>

                        <div className="w-8 h-px bg-gold-500/40 mb-5" />

                        <p className="text-white/50 leading-relaxed">
                          {member.description}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ─── CTA ─────────────────────────────────────────────────── */}
        <section className="relative bg-black overflow-hidden">
          {/* top fade from white */}
          <div className="absolute top-0 inset-x-0 h-24 bg-gradient-to-b from-white to-transparent pointer-events-none z-10" />

          {/* sparkles layer */}
          <div className="absolute inset-0 pointer-events-none">
            <SparklesCore
              id="cta-sparkles"
              background="transparent"
              minSize={0.3}
              maxSize={1.1}
              particleDensity={22}
              className="w-full h-full"
              particleColor="#D4AF37"
              speed={0.25}
            />
          </div>

          {/* large ambient glow */}
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
            <div className="w-[700px] h-[400px] rounded-full bg-yellow-500/10 blur-[140px]" />
          </div>

          {/* horizontal rule at top */}
          <div className="absolute top-24 inset-x-0 h-px bg-gradient-to-r from-transparent via-gold-500/30 to-transparent z-20" />

          <div className="relative z-20 max-w-5xl mx-auto px-6 pt-36 pb-28 flex flex-col items-center text-center">

            {/* eyebrow */}
            <motion.p
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="text-gold-400 text-xs tracking-[0.35em] uppercase font-medium mb-8"
            >
              Let's talk
            </motion.p>

            {/* headline */}
            <motion.h2
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              custom={0.05}
              className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.08] tracking-tight mb-7 max-w-3xl"
            >
              Ready to unlock{' '}
              <span className="gold-gradient bg-clip-text text-transparent gold-glow">
                what's possible?
              </span>
            </motion.h2>

            {/* sub-copy */}
            <motion.p
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              custom={0.1}
              className="text-white/50 text-lg md:text-xl leading-relaxed mb-14 max-w-xl"
            >
              No long sales process. No fluff. Just a conversation about what you need and how we can help.
            </motion.p>

            {/* CTA button with pulse ring */}
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              custom={0.15}
              className="relative"
            >
              {/* animated pulse rings */}
              <span className="absolute inset-0 -m-3 rounded-full border border-gold-400/20 animate-ping pointer-events-none" style={{ animationDuration: '2.4s' }} />
              <span className="absolute inset-0 -m-6 rounded-full border border-gold-400/10 animate-ping pointer-events-none" style={{ animationDuration: '2.4s', animationDelay: '0.4s' }} />
              <BookCallButton className="mx-auto" />
            </motion.div>

            {/* stat strip */}
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              custom={0.2}
              className="mt-20 flex flex-col items-center gap-2 border-t border-white/8 pt-14 w-full max-w-2xl"
            >
              <span className="text-3xl md:text-4xl font-bold gold-gradient bg-clip-text text-transparent">100%</span>
              <span className="text-white/35 text-xs tracking-wide text-center uppercase">Alberta-based team</span>
            </motion.div>

          </div>
        </section>

      </div>
    </div>
  );
};

export default AboutPage;
